import { TPagination } from 'src/@types';

export const MOBILE_MAX_WIDTH = 768;
export const MOBILE_FIXED_WIDTH = 450;
export const HEADER_HEIGHT = 64;
export const FOOTER_HEIGHT = 70;
export const PAGE_PADDING = 24;
export const BOTTOM_BTN_HEIGHT = 80;

// 공통코드 - 부모코드명
export const COMMON_CODE_NAME = {
  ACNT_STTS_CD: 'ACNT_STTS_CD',
  GENDER_TYPE_CD: 'GENDER_TYPE_CD',
  USER_TYPE_CD: 'USER_TYPE_CD',
  TERMS_TYPE_CD: 'TERMS_TYPE_CD',
  HSPTL_CD: 'HSPTL_CD',
  MED_QST_TYPE_CD: 'MED_QST_TYPE_CD',
  MEDICINE_CD: 'MEDICINE_CD',
  ALARM_TYPE_CD: 'ALARM_TYPE_CD',
  MISSION_TYPE_CD: 'MISSION_TYPE_CD',
  FTND_RESULT_CD: 'FTND_RESULT_CD',
  CRAVING_TYPE_CD: 'CRAVING_TYPE_CD',
  FAQ_TYPE_CD: 'FAQ_TYPE_CD',
  OS_TYPE_CD: 'OS_TYPE_CD',
  FILTER_TYPE_CD: 'FILTER_TYPE_CD',
};

// 부모코드ID
export const COMMON_CODE_PID = {
  FILTER_TYPE_CD: 6200,
};

// 약관 유형 코드
export const TERMS_TYPE_CODE = {
  SERVICE: 4000001,
  PRIVACY_POLICY: 4000002,
  REGISTER: 4000003,
  MARKETING: 4000004,
};

// 사용자 유형 코드
export const USER_TYPE_CD = {
  USER: 1007001,
  ADMIN: 1007002,
  MEDICALSTAFF: 1007003,
};

// 갈망 원인 카테고리 부모 코드
export const CRAVING_CATEGORY_PID = {
  SITUATION: 5900001,
  LOCATION: 5900002,
  WITH_WHOM: 5900003,
  EMOTION: 5900004,
} as const;

export type CravingCategoryPid = (typeof CRAVING_CATEGORY_PID)[keyof typeof CRAVING_CATEGORY_PID];

// 갈망원인 유형 코드
export const CRAVING_TYPE_CD = {
  STRESS: {
    code: 5900001,
    color: '#1E1C5D',
  },
  WORRY_DECISION: {
    code: 5900002,
    color: '#3831A1',
  },
  WHEN_BORED: {
    code: 5900003,
    color: '#554AEE',
  },
  WHEN_ANXIOUS: {
    code: 5900004,
    color: '#756CF9',
  },
  WHEN_FEELING_LONELY: {
    code: 5900005,
    color: '#034387',
  },
  DRINKING_OCCASIONS: {
    code: 5900006,
    color: '#05529F',
  },
  DURING_MEALS: {
    code: 5900007,
    color: '#0468C8',
  },
  TOBACCO_OFFER: {
    code: 5900008,
    color: '#2E96F9',
  },
  AROUND_SMOKERS: {
    code: 5900009,
    color: '#1A808C',
  },
  NICOTINE_WITH_DRAWAL_SYMPTOMS: {
    code: 5900010,
    color: '#20A4B1',
  },
  NICOTIN_DEPENDENCE_CRAVING: {
    code: 5900011,
    color: '#2FB49F',
  },
  HABITUALLY: {
    code: 5900012,
    color: '#FF643C',
  },
  UPON_WAKING_UP_IN_THE_MORNING: {
    code: 5900013,
    color: '#F9CA32',
  },
  AFTER_MEALS: {
    code: 5900014,
    color: '#FFA127',
  },
  IN_HAPPY_JOYFUL_SITUATIONS: {
    code: 5900015,
    color: '#D20540',
  },
  BREAK_TIME: {
    code: 5900016,
    color: '#FD4583',
  },
};

// SPEC-DIARY-CAUSE-001: 갈망 원인 leaf 코드 → 색상 (사용자 web 과 동일하게 매칭)
// 각 색은 사용자 web 의 갈망 원인 SVG 아이콘 배경색과 일치
export const CRAVING_CAUSE_LEAF_COLORS: Record<number, string> = {
  // Situation (pcode 5900001)
  5900001002: '#05529F', // 술자리
  5900001003: '#FFA127', // 식사 후
  5900001004: '#7B3507', // 커피 마실 때
  5900001005: '#FD4583', // 쉬는 시간
  5900001006: '#1A808C', // 흡연자 주변
  5900001007: '#F9CA32', // 기상 후
  5900001008: '#88238B', // 취침 전
  5900001009: '#28B252', // 출/퇴근
  5900001010: '#554AEE', // 지루함
  5900001999: '#98A2B3', // 기타
  // Location (pcode 5900002)
  5900002001: '#3491F5', // 집/베란다
  5900002002: '#D154B8', // 술집/바
  5900002003: '#AF6833', // 카페
  5900002004: '#3831A1', // 직장 근처
  5900002005: '#9CB95D', // 야외/공원
  5900002006: '#4CCFF0', // 차 안
  5900002999: '#98A2B3', // 기타
  // WithWhom (pcode 5900003)
  5900003001: '#A379E9', // 혼자
  5900003002: '#F36A71', // 친구
  5900003003: '#02BBD9', // 회사 동료
  5900003004: '#DD316E', // 가족
  5900003005: '#FD4583', // 연인
  5900003999: '#98A2B3', // 기타
  // Emotion (pcode 5900004) — 차트에서 제외되지만 호환용으로 정의
  5900004001: '#D20540',
  5900004002: '#30547D',
  5900004003: '#756CF9',
  5900004004: '#ED3B00',
  5900004005: '#1E1C5D',
  5900004999: '#B2BDCF',
};

// 금연변화동기평가상태 유형 코드
export const KSCMS_STATUS_TYPE_CODE = {
  PREREFLECTION: 5800001,
  REFLECTION: 5800002,
  PREPARATION: 5800003,
};

export const DEFAULT_PAGE_SIZE = 25;
export const DEFAULT_PAGINATION: TPagination = {
  page: 1,
  size: DEFAULT_PAGE_SIZE,
  offset: 0,
  numberOfElements: 0,
  totalElements: 0,
  totalPages: 0,
  first: true,
  last: false,
};

export const LANGUAGE = {
  korean: 'ko',
  english: 'en',
  deutsch: 'de',
};
